import { type LucideIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type IconActionVariant = 'default' | 'destructive' | 'success';

const variantStyles: Record<IconActionVariant, string> = {
  default: 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
  destructive:
    'border-border text-red-600 hover:border-red-200 hover:bg-red-50 dark:text-red-400 dark:hover:border-red-900 dark:hover:bg-red-950',
  success:
    'border-border text-green-600 hover:border-green-200 hover:bg-green-50 dark:text-green-400 dark:hover:border-green-900 dark:hover:bg-green-950',
};

type IconActionSize = 'sm' | 'md';

const sizeStyles: Record<IconActionSize, { button: string; icon: string }> = {
  sm: { button: 'h-7 w-7', icon: 'h-3.5 w-3.5' },
  md: { button: 'h-8 w-8', icon: 'h-4 w-4' },
};

interface IconActionButtonProps {
  icon: LucideIcon;
  /** Tooltip text — also used as the accessible label. */
  tooltip: string;
  onClick?: () => void;
  variant?: IconActionVariant;
  /** `md` (default) for card/section headers, `sm` for list-row / inline actions. */
  size?: IconActionSize;
  disabled?: boolean;
  className?: string;
}

/**
 * Consistent round, icon-only action button with a tooltip.
 * The canonical action affordance across profile cards/sections.
 */
export function IconActionButton({
  icon: Icon,
  tooltip,
  onClick,
  variant = 'default',
  size = 'md',
  disabled,
  className,
}: IconActionButtonProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={tooltip}
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full border bg-card shadow-sm transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-60',
              sizeStyles[size].button,
              variantStyles[variant],
              className
            )}
          >
            <Icon className={sizeStyles[size].icon} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
